import { useEffect } from "react";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { Button, Card, CardBody, CardHeader, Checkbox, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Select, SelectItem, Table, TableBody, TableCell, TableColumn, Textarea, TableHeader, TableRow, useDisclosure } from "@nextui-org/react";
import ControlledInput from "../../../../elements/ControlledInput";
import toast from "react-hot-toast";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { isResponseErrorObject } from "../../../../../services/helpers";
import useSWR from "swr";
import { getFetcher, postFetcher } from "../../../../../services/api";
import { yupResolver } from "@hookform/resolvers/yup";
import ControlledReactSelect from "../../../../elements/ControlledReactSelect";
import useListPic from "../../../../../hooks/useListPic";
import DataPICSelector from "../ModalDataPIC";
import { customerValidation } from "../../../../../services/validation/customer.validation";

export default function InlineEditCustomer({ selectedCompany, control, isDisabled = false, setValue }) {
  const distributionSum = useWatch({ control, name: "dataCustomers" });
  const { isOpen: isOpenModalForm, onOpen: onOpenModalForm, onOpenChange: onOpenChangeModalForm, onClose: onCloseModalForm } = useDisclosure();
  const { fields, append, remove } = useFieldArray({ control, name: "dataCustomers" });
  const {
    register: registerInline,
    control: controlInline,
    watch: watchInline,
    handleSubmit: handleSubmitInline,
    setValue: setValueInline,
    reset: resetInline,
    formState: { isSubmitting: isSubmittingInline, errors: errorsInline },
  } = useForm({ resolver: yupResolver(customerValidation) });

  const provinceId = watchInline("provinceId");
  const regencyId = watchInline("regencyId");
  const districtId = watchInline("districtId");

  const { data: dataCustomer, isLoading: isLoadingCustomer, mutate: mutateCustomer } = useSWR(selectedCompany ? `/api/company/search-customer?returnAll=true&companyId=${selectedCompany}` : null, getFetcher);
  const { data: province } = useSWR("/api/location/provinces", getFetcher);
  const { data: regency } = useSWR(provinceId ? `/api/location/cities?provinceId=${provinceId}` : null, getFetcher);
  const { data: district } = useSWR(regencyId ? `/api/location/districts?regencyId=${regencyId}` : null, getFetcher);
  const { data: village } = useSWR(districtId ? `/api/location/villages?districtId=${districtId}` : null, getFetcher);
  const { dataPIC, isLoadingDataPIC, mutatePIC } = useListPic({ companyId: selectedCompany });

  /*
   * companyId harus ada di state react-hook-form, bukan cuma sebagai nilai di
   * DOM. Sama seperti pada modal Tambah Perusahaan Transporter: prop value pada
   * input tersembunyi tidak pernah memicu event perubahan, sehingga setelah form
   * di-reset sekali, validasi yup gagal "Harus diisi" pada kolom tak kasat mata
   * dan tombol Tambah tampak mati tanpa pesan apa pun.
   */
  useEffect(() => {
    setValueInline("companyId", selectedCompany ?? "");
  }, [selectedCompany, isOpenModalForm, setValueInline]);

  function onCloseForm() {
    resetInline({
      namaCustomer: "",
      alamat: "",
      alamat: "",
      email: "",
      telepon: "",
      fax: "",
      latitude: "",
      longitude: "",
      provinceId: "",
      regencyId: "",
      districtId: "",
      villageId: "",
      dataPICIds: [],
      companyId: selectedCompany ?? "",
      diLuarPP73: false,
      tujuanPenggunaan: "",
    });
    onCloseModalForm();
  }

  async function onSubmitForm(formData) {
    try {
      delete formData.companyId;
      const newPayload = formData;
      const response = await postFetcher(selectedCompany ? `/api/company/create-data-customer/${selectedCompany}` : null, newPayload);
      mutateCustomer();
      append({ customerId: response.id, jumlahB3Distribusi: 0 });
      toast.success("Perusahaan customer berhasil ditambah!");
      onCloseForm();
    } catch (error) {
      isResponseErrorObject(error.response.data.message)
        ? Object.entries(error.response.data.message).forEach(([key, value]) => {
            toast.error(value);
          })
        : toast.error(error.response.data.message);
    }
  }

  const totalDistribution = Array.isArray(distributionSum)
    ? distributionSum.reduce((total, field) => {
        const value = parseFloat(field.jumlahB3Distribusi) || 0;
        // Bulatkan agar galat floating point (0.1 + 0.2 = 0.30000000000000004) tidak ikut tampil/tersimpan
        const sum = Number((total + value).toFixed(6));
        setValue("jumlahB3Distribusi", sum);
        return sum;
      }, 0)
    : 0;

  return (
    <>
      <>
        <Card radius="sm">
          <CardHeader className="w-full flex justify-between">
            <div className="text-sm">
              Perusahaan Customer <span className="text-danger ml-0.5">*</span>
            </div>
            <Button isDisabled={isDisabled} onPress={onOpenModalForm} color="primary" startContent={<PlusIcon className="size-4" />}>
              Tambah Perusahaan Customer
            </Button>
          </CardHeader>
          <CardBody>
            <div className="flex items-center gap-3 mb-3">
              <Button size="sm" isDisabled={isDisabled} onPress={() => append({ customerId: "", jumlahB3Distribusi: 0 })} color="primary" startContent={<PlusIcon className="size-4" />}>
                Tambah Distribusi
              </Button>
              <span className="text-sm">
                Total Distribusi: <strong>{totalDistribution}</strong>
              </span>
            </div>
            <Table removeWrapper aria-label="table-customer">
              <TableHeader>
                <TableColumn>Nama</TableColumn>
                <TableColumn>Jumlah Distribusi</TableColumn>
                <TableColumn>Aksi</TableColumn>
              </TableHeader>
              <TableBody>
                {fields.map((field, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <ControlledReactSelect label="" name={`dataCustomers[${index}].customerId`} control={control} options={dataCustomer?.data?.map((item) => ({ value: item.id, label: item.namaCustomer }))} isLoading={isLoadingCustomer} />
                    </TableCell>
                    <TableCell>
                      <ControlledInput type="number" label="" name={`dataCustomers[${index}].jumlahB3Distribusi`} isRequired={true} control={control} />
                    </TableCell>
                    <TableCell>
                      <Button color="danger" size="sm" isIconOnly onClick={() => remove(index)} isDisabled={fields.length === 1}>
                        <TrashIcon className="size-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardBody>
        </Card>
      </>

      <Modal scrollBehavior="inside" size="3xl" isOpen={isOpenModalForm} onOpenChange={onOpenChangeModalForm} onClose={onCloseForm} isDismissable={false} isKeyboardDismissDisabled={false}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>Tambah Perusahaan Customer</ModalHeader>
              <ModalBody>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                  {/* Penanda ini ditaruh paling atas, tepat di bawah judul:
                      statusnya menentukan cara membaca seluruh isian di
                      bawahnya, jadi pengisi perlu melihatnya lebih dulu. */}
                  <div className="col-span-2">
                    <Controller
                      control={controlInline}
                      name="diLuarPP73"
                      defaultValue={false}
                      render={({ field }) => (
                        <Checkbox size="sm" isSelected={!!field.value} onValueChange={field.onChange}>
                          <span className="text-sm">Pelanggan berada di luar PP 73</span>
                        </Checkbox>
                      )}
                    />
                  </div>

                  <ControlledInput label="Pelanggan" name="namaCustomer" isRequired={true} control={controlInline} />
                  <ControlledInput label="Alamat" name="alamat" isRequired={true} control={controlInline} />
                  <ControlledInput label="Email" name="email" isRequired={true} control={controlInline} />
                  <ControlledInput label="Telepon" name="telepon" isRequired={true} control={controlInline} />
                  <ControlledInput label="Fax" name="fax" isRequired={true} control={controlInline} />
                  <ControlledInput label="Latitude" name="latitude" isRequired={true} control={controlInline} />
                  <ControlledInput label="Longitude" name="longitude" isRequired={true} control={controlInline} />
                  <ControlledReactSelect control={controlInline} label="Provinsi" name="provinceId" isRequired={true} options={province?.map((item) => ({ value: item.id, label: item.name }))} placeholder="Pilih..." />
                  <ControlledReactSelect control={controlInline} label="Kabupaten" name="regencyId" isRequired={true} options={regency?.map((item) => ({ value: item.id, label: item.name }))} placeholder="Pilih..." />
                  <ControlledReactSelect control={controlInline} label="Kecamatan" name="districtId" isRequired={true} options={district?.map((item) => ({ value: item.id, label: item.name }))} placeholder="Pilih..." />
                  <ControlledReactSelect control={controlInline} label="Kelurahan" name="villageId" isRequired={true} options={village?.map((item) => ({ value: item.id, label: item.name }))} placeholder="Pilih..." />
                  <div className="col-span-2">
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <ControlledReactSelect control={controlInline} label="Data PIC" name="dataPICIds" isLoading={isLoadingDataPIC} options={dataPIC?.map((item) => ({ value: item.id, label: item.namaPIC }))} isMulti={true} />
                      </div>
                      <DataPICSelector type={"customer"} mutatePIC={mutatePIC} selectedCompany={selectedCompany} />
                    </div>
                  </div>

                  <div className="col-span-2">
                    <Controller
                      control={controlInline}
                      name="tujuanPenggunaan"
                      defaultValue=""
                      render={({ field, fieldState }) => (
                        <Textarea
                          {...field}
                          variant="faded"
                          label="Tujuan Penggunaan"
                          labelPlacement="outside"
                          placeholder="Catatan untuk apa B3 ini digunakan pelanggan tersebut..."
                          minRows={2}
                          isInvalid={!!fieldState.error}
                          errorMessage={fieldState.error?.message}
                        />
                      )}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button onPress={handleSubmitInline(onSubmitForm)} isLoading={isSubmittingInline} isDisabled={isSubmittingInline} type="submit" color="primary">
                    Tambah
                  </Button>
                  <Button isDisabled={isSubmittingInline} color="danger" variant="faded" onPress={onClose}>
                    Batal
                  </Button>
                </div>
              </ModalBody>
              <ModalFooter></ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
}
